import React, {useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography} from "@mui/material";
import {selectUserAccount, selectUserPictureUrl} from "../../../stores/account/account.selectors";
import {FormattedMessage} from "react-intl";
import {setLastPictureUpdate} from "../../../stores/account/account.slice";
import {uploadPictureAsync} from "../../../stores/tradefan/tradefan.api";
import {canvasPreview} from './CanvasPreview'
import {useDebounceEffect} from './useDebounceEffect'
import CloseIcon from "@mui/icons-material/Close";
import ReactCrop from 'react-image-crop'

import 'react-image-crop/dist/ReactCrop.css'
import './ChangePicturePopUp.style.scss'
const ChangePicturePopUp = ({id, title, subTitle, open, onClose, onCancel, onOk, options, data}) => {
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [aspect, setAspect] = useState(4/4);
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const dispatch = useDispatch();
    const userAccount = useSelector(selectUserAccount);
    const userPicUrl = useSelector((state) => selectUserPictureUrl(state, userAccount.accountId));

    const getImageUrl = async () => {
        const image = imgRef.current
        const previewCanvas = previewCanvasRef.current
        if (!image || !previewCanvas || !completedCrop) {
            throw new Error('Crop canvas does not exist')
        }

        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY
        )
        const ctx = offscreen.getContext('2d')
        if (!ctx) {
            throw new Error('No 2d context')
        }

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height
        )

        const blob = await offscreen.convertToBlob({
            type: 'image/png',
        })

        let formFile = new FormData();
        formFile.append('image', blob);
        formFile.append('fileName', "Img");

        return {
            formFile,
            fileUrl: window.URL.createObjectURL(blob)
        };
    }

    const onSaveChangesClick = async () => {

        const file = await getImageUrl();
        if (file) {
            uploadPictureAsync(file.formFile)
                .then(res => {
                    if (res.status == "error") {
                        //Call another error PopUp
                        return;
                    }
                    dispatch(setLastPictureUpdate())
                    //Close all popups
                    onClose();
                    //Small Hack to force userPic refresh
                    window.userPicVersion = new Date().getTime();
                    //Call another success PopUp

                }).catch(err => {
                console.error(err);
            });
        }
    }

    const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => {
        const cropWidth = mediaWidth * 0.9;
        const cropHeight = cropWidth / aspect;

        return {
            unit: 'px',
            width: cropWidth,
            height: cropHeight,
            x: (mediaWidth - cropWidth) / 2,
            y: (mediaHeight - cropHeight) / 2,
        };
    };

    const onSelectFile = e => {
        if (e.target.files && e.target.files.length > 0) {
            setCrop(undefined)
            const reader = new FileReader()
            reader.addEventListener('load', () =>
                setImgSrc(reader.result?.toString() || '')
            )
            reader.readAsDataURL(e.target.files[0])
        }
    };

    const onImageLoad = (e) => {
        if (aspect) {
            const { width, height } = e.currentTarget
            setCrop(centerAspectCrop(width, height, aspect))
        }
    }

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                    rotate
                )
            }
        },
        100,
        [completedCrop, scale, rotate]
    )

    return (
        <Dialog className={"ChangePicturePopUp"} open={open} onClose={onClose} maxWidth="xs">
            <IconButton
                className="closeButton"
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="close">
                <CloseIcon/>
            </IconButton>
            <DialogTitle className={"dialogTitle"}>
                <FormattedMessage id={"popUp.upLoadPicture.title"}/>
            </DialogTitle>
            <DialogContent className={"dialogContent"}>
                {!imgSrc && <Avatar
                    alt="Profile"
                    src={userPicUrl}
                    sx={{width: 150, height: 150}}
                />}
                {!!imgSrc && (
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={aspect}
                    >
                        <img
                            ref={imgRef}
                            alt="Crop me"
                            src={imgSrc}
                            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                            onLoad={onImageLoad}
                        />
                    </ReactCrop>
                )}
                <br/>
                {!!completedCrop && (
                    <>
                        <div>
                            <canvas
                                ref={previewCanvasRef}
                                style={{
                                    border: '1px solid black',
                                    objectFit: 'contain',
                                    width: "40px",
                                    height: "40px",
                                }}
                            />
                        </div>
                    </>
                )}
                <Typography variant="body2">
                    <FormattedMessage id={"popUp.upLoadPicture.imageFormat"}/>
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id={"popUp.upLoadPicture.maxSize"}/>
                </Typography>
                {!imgSrc && <Button className="browseButton">
                    <label htmlFor="file-input">
                        <FormattedMessage id={"popUp.upLoadPicture.browseFiles"}/>
                    </label>
                    <input hidden
                           name="file-input"
                           id="file-input"
                           accept="image/*"
                           type="file"
                           onChange={onSelectFile}
                    />
                </Button>}
                {imgSrc && <DialogActions>
                    <Button onClick={onSaveChangesClick} className="saveButton" variant="contained">
                        <FormattedMessage id={"popUp.upLoadPicture.saveChanges"}/>
                    </Button>
                </DialogActions>}
            </DialogContent>

        </Dialog>
    );
}

export default ChangePicturePopUp;